import { forkJoin, Observable, of, OperatorFunction } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { UserDataProvider } from './user.data-provider';
import { BasketPageModel } from '../model/page/basket/basket.page.model';
import { ScreenHttpService } from '../http/screen.http.service';
import { OrderTicketHttpService } from '../http/order-ticket.http.service';
import { EventDataProvider } from './event.data-provider';
import { OrderStatus, OrderViewModel } from '../model/view-model/order/order.view.model';
import { storageKey } from '../../app.const';
import { ScreeningViewModel } from '../model/view-model/screening/screening.view.model';
import { TicketViewModel } from '../model/view-model/shared/ticket/ticket.view.model';
import { TicketApiModel } from '../model/api-model/shared/ticket/ticket.api.model';
import { EventViewModel } from '../model/view-model/event/event.view.model';
import { ScreenApiModel } from '../model/api-model/screen/screen.api.model';
import { ScreenViewModel } from '../model/view-model/screen/screen.view.model';
import { StateService } from '../state/state.service';
import { GenreViewModel } from '../model/view-model/genre/genre.view.model';
import { ScreeningDataProvider } from './screening.data-provider';
import { ScreeningItemViewModel } from '../model/view-model/order/screening-item/screening-item.view.model';
import { LoaderEnum, LoaderService } from '@lib/core';

const ensureAgreementsAndMapToBasket = <T>(
  basketModel: BasketPageModel,
  userDataProvider: UserDataProvider,
  loaderService: LoaderService
): OperatorFunction<T, BasketPageModel> =>
  switchMap(() => {
    if (basketModel.agreements) {
      loaderService.hideLoader(LoaderEnum.BASKET_SUMMARY);
      return of(basketModel);
    }

    return userDataProvider.getAgreements().pipe(
      tap((agreements) => {
        basketModel.agreements = agreements.marketingAgreements;
        loaderService.hideLoader(LoaderEnum.BASKET_SUMMARY);
      }),
      map(() => basketModel)
    );
  });

@Injectable({
  providedIn: 'root'
})
export class BasketDataProvider {
  public basketPageModel: BasketPageModel;

  constructor(
    private screenHttpService: ScreenHttpService,
    private orderTicketHttpService: OrderTicketHttpService,
    private screeningDataProvider: ScreeningDataProvider,
    private userDataProvider: UserDataProvider,
    private eventDataProvider: EventDataProvider,
    private stateService: StateService,
    protected loaderService: LoaderService
  ) {
    this.basketPageModel = new BasketPageModel();
  }

  public updateBasket(order: OrderViewModel, cinemaId: string = order.cinemaId): Observable<BasketPageModel> {
    this.loaderService.showLoader(LoaderEnum.BASKET_SUMMARY);
    const basketModel = this.basketPageModel ?? new BasketPageModel();
    const isEvent = this.stateService.getItem(storageKey.isEvent) === 'true';
    const screeningId: string = !!order?.screeningItems?.length
      ? order?.screeningItems[0]?.screeningId
      : (this.stateService.getItem(storageKey.lastScreeningId) ??
        JSON.parse(this.stateService.getItem(storageKey.parentOrder))?.parentOrderScreening?.id ??
        null);
    const eventId: string = isEvent ? this.stateService.getItem(storageKey.lastEventId) : null;

    if (!(eventId ?? screeningId)) {
      return of(basketModel).pipe(ensureAgreementsAndMapToBasket(basketModel, this.userDataProvider, this.loaderService));
    }

    const getScreeningModelOrNull = (cinemaId: string, screeningId: string) => {
      return isEvent ? of(null) : this.screeningDataProvider.getCinemaScreening(cinemaId, screeningId);
    };

    const getEventModelOrNull = (cinemaId: string, eventId: string, screeningId: string) => {
      const eventDate = this.stateService.getItem(storageKey.chosenDate) ? DateTime.fromISO(this.stateService.getItem(storageKey.chosenDate)) : null;
      return isEvent ? this.eventDataProvider.getCinemaEvent(cinemaId, eventId, screeningId, eventDate?.startOf('day'), eventDate?.endOf('day')) : of(null);
    };

    let genres: GenreViewModel[] = [];
    const basketModelBuilder = forkJoin({
      screeningModel: getScreeningModelOrNull(cinemaId, eventId ?? screeningId),
      eventModel: getEventModelOrNull(cinemaId, eventId, screeningId)
    }).pipe(
      tap((data) => {
        basketModel.screeningId = eventId ?? screeningId;

        if (data.screeningModel && !data.eventModel) {
          //screening way
          basketModel.screening = data.screeningModel;
          genres = data.screeningModel.movieCopy?.movie?.genres;
        } else {
          //event way
          basketModel.event = data.eventModel;
          basketModel.screening = this.mapEventToScreening(basketModel.event);
          genres = data.eventModel.genres;
        }

        basketModel.movieCopy = basketModel.screening.movieCopy;
        basketModel.movie = basketModel.movieCopy?.movie;
      }),
      switchMap(() =>
        this.screenHttpService.getScreens(cinemaId, screeningId).pipe(
          tap((screenApiModels: ScreenApiModel[]) => {
            const firstScreenApiModel = screenApiModels[0];
            basketModel.screen = this.mapScreenData(firstScreenApiModel, basketModel, genres);
          })
        )
      ),
      switchMap(() =>
        this.getTicketsWithAssignedSeatIds(cinemaId, order).pipe(
          tap((tickets) => {
            basketModel.tickets = tickets;
          })
        )
      )
    );

    return basketModelBuilder.pipe(ensureAgreementsAndMapToBasket(basketModel, this.userDataProvider, this.loaderService));
  }

  private getTicketsWithAssignedSeatIds(cinemaId: string, order: OrderViewModel): Observable<TicketViewModel[]> {
    if (!order.screeningItems?.length) {
      return of([]);
    }

    if (order.status === OrderStatus.CANCELLED) {
      const localTickets = this.extractTicketsFromScreeningItems(order.screeningItems);
      const ticketViewModels = localTickets.map((ticket) => new TicketViewModel(ticket));
      return of(ticketViewModels);
    } else {
      // const everyItemsHaveTickets = order.screeningItems.every((item) => !!item.ticketId);
      // if (!everyItemsHaveTickets) {
      //   OrderTicketHttpService.cacheBuster$.next();
      // }

      const screeningItemsMap = new Map<string, ScreeningItemViewModel>(order.screeningItems.map((item) => [item.id, item]));
      return this.orderTicketHttpService.getOrderTickets(cinemaId, order.id).pipe(
        map((apiTickets: TicketApiModel[] | null) => {
          if (!apiTickets) {
            return [];
          }

          return apiTickets.map((ticket) => {
            const matchingItem = screeningItemsMap.get(ticket.screeningItemId);
            const enrichedTicketData = {
              ...ticket,
              seatId: matchingItem?.seatId ?? null
            };

            return new TicketViewModel(enrichedTicketData);
          });
        })
      );
    }
  }

  private mapEventToScreening(eventModel: EventViewModel): ScreeningViewModel {
    const screening = new ScreeningViewModel();
    screening.id = eventModel.screeningId;

    screening.audience = eventModel.audience;
    screening.availabilityStatus = eventModel.availabilityStatus;
    screening.cinemaId = eventModel.cinemaId;
    screening.maxOccupancy = eventModel.maxOccupancy;
    screening.screenId = eventModel.screenId;

    screening.screeningTimeFrom = eventModel.screeningTimeFrom;
    screening.generalAdmission = eventModel.generalAdmission;
    screening.screeningDuration = eventModel.duration;
    screening.isScreenSwapping = false;
    screening.reservationTimeTo = eventModel.reservationTimeTo;
    screening.saleTimeTo = eventModel.saleTimeTo;
    screening.screeningTimeFrom = eventModel.screeningTimeFrom;
    screening.screeningTimeTo = eventModel.timeTo;

    if (eventModel.movieCopy) {
      screening.movieCopy = eventModel.movieCopy;
      screening.moviePrintId = screening.movieCopy.id;
    }

    return screening;
  }

  private mapScreenData(screenApiModel: ScreenApiModel, basketModel: BasketPageModel, genres: GenreViewModel[]): ScreenViewModel {
    const screen = new ScreenViewModel(screenApiModel);
    screen.movieDate = basketModel.screening.screeningTimeFrom;
    screen.generalAdmission = basketModel.screening.generalAdmission;
    screen.movieGenres = genres || [];
    screen.screeningDuration = basketModel.screening.screeningDuration;
    screen.isScreenSwapping = basketModel.screening.isScreenSwapping;
    screen.screeningAvailabilityStatus = basketModel.screening.availabilityStatus;
    screen.reservationTimeTo = basketModel.screening.reservationTimeTo;
    screen.saleTimeTo = basketModel.screening.saleTimeTo;
    screen.screeningTimeFrom = basketModel.screening.screeningTimeFrom;
    screen.screeningTimeTo = basketModel.screening.screeningTimeTo;

    if (basketModel.event) {
      screen.isEvent = true;
      // event
      screen.movieName = basketModel.event.name;
      screen.movieDuration = basketModel.event.duration;
      screen.posterUrl = basketModel.event.posters?.[0] || '';
      screen.pictureUrl = basketModel.event.pictures?.[0] || '';
      screen.movieDescription = basketModel.event.description;
      screen.movieTags = basketModel.event.tagGroups || [];
      screen.movieRating = basketModel.event.ratings;
      screen.movieCopyRelease = basketModel.event.release;
    } else {
      // movie
      screen.movieName = basketModel.movie.title;
      screen.moviePremiere = basketModel.movie.premiereDate;
      screen.movieDuration = basketModel.movie.duration;
      screen.posterUrl = basketModel.movie.posters?.[0] || '';
      screen.pictureUrl = basketModel.movie.pictures?.[0] || '';
      screen.movieDescription = basketModel.movie.description;
      screen.movieTags = basketModel.movie.tagGroups || [];
      screen.movieRating = basketModel.movie.ratings;
    }

    // moviePrint
    if (basketModel.movieCopy) {
      screen.movieLanguage = basketModel.movieCopy.language;
      screen.movieSpeakingType = basketModel.movieCopy.speakingType;
      screen.moviePrintType = basketModel.movieCopy.printType;
      screen.movieSoundType = basketModel.movieCopy.soundType;
      screen.movieCopyRelease = basketModel.movieCopy.release;
    }

    return screen;
  }

  private extractTicketsFromScreeningItems(screeningItems: ScreeningItemViewModel[]): TicketApiModel[] {
    if (!screeningItems) {
      return [];
    }

    return screeningItems
      .filter((item) => item.ticketId)
      .map(
        (item) =>
          ({
            id: item.ticketId,
            name: item.name,
            price: item.price,
            screeningItemId: item.id,
            tax: item.tax,
            defaultPriceLevelPrice: item.defaultPriceLevelPrice
          }) as TicketApiModel
      );
  }
}
